/**
 * Created by Ning Jiang on 2023-12-12.
 */

import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {TimelineLite, TweenLite} from "gsap";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";
import {EGRStackedSymbol} from "./EGRStackedSymbol";
import {ElGordosRevengeSymbolState} from "./ElGordosRevengeSymbolConfig";
import {ElGordosRevenge} from "../ElGordosRevenge";
import {ElGordosRevengeGameAssets} from "../ElGordosRevengeGameAssets";
import {ElGordosRevengeSymbol} from "./ElGordosRevengeSymbol";
import {ElGordosRevengeSound} from "../sound/ElGordosRevengeSound";
import {ElGordosRevengeLayers, Layers} from "../view/ElGordosRevengeLayers";
import {ElGordosRevengeGameConfig} from "../ElGordosRevengeGameConfig";

export class EGRStackedWild extends EGRStackedSymbol {

    protected _xRIPAnimationName:string = "xRIP3";

    constructor(symbolName:string, reelId:number, initState:ElGordosRevengeSymbolState) {
        super(symbolName, reelId, initState);
    }

    public getSymbolShotRect():PIXI.Rectangle {
        return new PIXI.Rectangle(this._centerPosition.x - 50, this._centerPosition.y - 266, 100, 532);
    }

    protected getStateTexture(state?:ElGordosRevengeSymbolState):PIXI.Texture[] {
        // if WXN is the stacked symbol with different looks on different reels, then this will make the WXN on reel 1 to load image WXN1/WXN1.png
        let texturePath = this.stackedSymbolName.symName.includes("M") ? `${this.stackedSymbolName.symName}/${this.stackedSymbolName.symName}` : `${this.stackedSymbolName.symName}${this.reelId}/${this.stackedSymbolName.symName}${this.reelId}`;
        if(state === ElGordosRevengeSymbolState.SPIN) {
            texturePath += `_${state}`;
        }

        return ElGordosRevenge.assetLoader.getTextures(texturePath);
    }

    protected initAnimation() {
        super.initAnimation();
        this._multiplierContainer.position.set(this._centerPosition.x, this._centerPosition.y + ElGordosRevengeSymbol.DEFAULT_HEIGHT + 20);
        this._multiplierText.x = 0;
    }

    protected getMultiplierText():PIXI.BitmapText {
        const text = new PIXI.BitmapText("", {
            fontName : ElGordosRevengeGameAssets.BITMAP_FONT_NUDGE_MULTIPLIER,
            fontSize : 35,
            tint : ElGordosRevengeGameConfig.MULTIPLIER_TEXT_TINT
        });
        (<PIXI.Point>text.anchor).set(0.5, 1.38);
        text.letterSpacing = -12;
        return text;
    }

    protected getOnShotAnimationName():string {
        return "4xWILD_Shoot";
    }

    protected getKillAnimationName():string {
        if(this.symbolName.includes("M")) {
            return `${this.stackedSymbolName.symName}_Dead`;
        }
        return `WXN${this.reelId}_Dead`;
    }

    protected getDeadLoopAnimationName():string {
        if(this.symbolName.includes("M")) {
            return `${this.stackedSymbolName.symName}_DeadLoop`;
        }
        return `WXN${this.reelId}_DeadLoop`;
    }

    protected getIndividualAnimationName():string {
        if(this.symbolName.includes("M")) {
            return `${this.stackedSymbolName.symName}_Toggle`;
        }
        return `WXN${this.reelId}_Toggle`;
    }

    protected getCharacterAssetName():string {
        if(this.symbolName.includes("M")) {
            return this.symbolName.slice(0, 2);
        }
        return "WXN";
    }

    public showMultiplier(value:number, animate:boolean):TimelineLite {
        Logger.logDev(`wild update to multiplier ${value}`);
        const tl = new TimelineLite();
        if(value === this.currentMultiplier) {
            return tl;
        }

        (<EGRStackedWild[]>this.stack?.symbols).forEach((w:EGRStackedWild) => {
            if(w) {
                w._currentMultiplier = value;
            }
        })

        tl.add(() => {
            (<EGRStackedWild[]>this.stack?.symbols).forEach((w:EGRStackedWild) => {
                if(w) {
                    w._multiplierText.text = `x${value}`;
                    w._multiplierText.scale.set(1, 0.7);
                    w._multiplierText.updateTransform();
                    if(w._multiplierText.textWidth > 100) {
                        const targetScale = MathHelper.roundToDecimals(100 / w._multiplierText.textWidth, 2);
                        w._multiplierText.scale.set(targetScale, targetScale * 0.7);
                    }
                }
            })
        }, 0);

        if(animate) {
            tl.add(() => {
                this._multiplierContainer.scale.set(0.75, 0.525);
            });
            tl.add(TweenLite.to(this._multiplierContainer.scale, 0.166, {x : 1.25, y : 1.25}));
            tl.add(TweenLite.to(this._multiplierContainer.scale, 0.133, {x : 0.95, y : 0.95}));
            tl.add(TweenLite.to(this._multiplierContainer.scale, 0.133, {x : 1, y : 1}));
        }

        return tl;
    }

    protected addSplitSymbolAnimation(tl:TimelineLite, oldSymbolSize:number, type:number, noWin:boolean):void {
        const targetMultiplier = this.currentMultiplier * 2;
        if(!noWin) {
            const splitSpine = new PIXI.spine.Spine(ElGordosRevenge.assetLoader.getSpineAsset("split"));
            splitSpine.autoUpdate = true;
            const globalPosition = this.getGlobalCenterPosition();
            splitSpine.position.set(globalPosition.x + this._centerPosition.x, globalPosition.y + this._centerPosition.y + ElGordosRevengeSymbol.DEFAULT_HEIGHT * 3);
            tl.add(() => {
                ElGordosRevengeLayers.getLayer(Layers.WIN_PRESENTATIONS)!.addChild(splitSpine);
                splitSpine.state.setAnimation(0, "animation", false);
            }, 0);
            tl.add(() => {
                splitSpine.parent.removeChild(splitSpine);
                splitSpine.destroy();
            }, splitSpine.spineData.findAnimation("animation").duration);
        }

        tl.add([
            this.showMultiplier(targetMultiplier, this.visible),
            () => {
                this.updateLayers();
                ElGordosRevenge.multiplier.updateReelMultiplier(this.reelId, targetMultiplier, true);
            }
        ], noWin ? 0.01 : this.splitSplashDelay);
    }

    public cylinderSplit(reelId:number, symbolId:number, isRestore:boolean):TimelineLite {
        const tl = new TimelineLite();
        if(isRestore) {
            return tl;
        }
        const targetMultiplier = this.currentMultiplier * 2;
        tl.add([
            this.showMultiplier(targetMultiplier, true),
            () => {
                ElGordosRevenge.multiplier.updateReelMultiplier(this.reelId, targetMultiplier, true);
            }
        ]);
        return tl;
    }

    public playLanding(init:boolean = false):TimelineLite {
        const tl = new TimelineLite();
        const initMulti = ElGordosRevenge.fsMeter.getCurrentLevelMultiplier();
        if(init && !this.visible) {
            return tl;
        }
        this.createDeadContainer();
        const aniName = `WXN${this.reelId}_Landing`;
        tl.add(() => {
            this._deadCharacter.visible = true;
            this._deadCharacter.state.setAnimation(0, aniName, false);
        });
        if(!init) {
            tl.add([
                this.showMultiplier(initMulti, true),
                () => {
                    ElGordosRevenge.multiplier.updateReelMultiplier(this.reelId, initMulti, true);
                    ElGordosRevengeSound.playNudgeLandSound(this.reelId);
                }
            ]);
        }
        tl.add(() => {}, `${this._deadCharacter.spineData.findAnimation(aniName).duration}`);
        return tl;
    }

    public getNudgeAnimation(lastStep:boolean, aborted:boolean, hasWin:boolean):TimelineLite {
        const tl = new TimelineLite();

        this.createDeadContainer();
        let aniName:string;
        if(this.symbolName.includes("M")) {
            aniName = `${this.stackedSymbolName.symName}_Nudge`;
        } else {
            aniName = `WXN${this.reelId}_Nudge`;
        }
        if(lastStep) {
            aniName += `Last`;
        }
        if(aniName.length > 0) {
            tl.add(() => {
                this._deadCharacter.visible = true;
                this._deadCharacter.state.setAnimation(0, aniName, false);
            });
            tl.add(() => {}, `+=${this._deadCharacter.spineData.findAnimation(aniName).duration}`);
        }
        return tl;
    }

    protected playKillSound():void {
        if(this.symbolName.includes("M")) {
            ElGordosRevengeSound.playSymbolKill(`${this.stackedSymbolName.symName.slice(0, 2)}`, this.reelId);
        } else {
            ElGordosRevengeSound.playSymbolKill(`WXN${this.reelId}`, this.reelId);
        }
    }
}